import { useRef, useState } from "react"
import styles from "./styles.module.scss"

interface ConfirmationCodeInputProps {
  codeLength: number
  onCodeEntered: (code: string) => void
  isReadOnly?: boolean
}

export function ConfirmationCodeInput({
  codeLength,
  onCodeEntered,
  isReadOnly,
}: ConfirmationCodeInputProps) {
  const defaultCodes = Array(codeLength).fill("") as string[]

  const [codes, setCodes] = useState<string[]>(defaultCodes)
  const refs = useRef<(HTMLInputElement | null)[]>([])

  const onChangeCode = (text: string, index: number) => {
    if (text.length > 1 && text.length !== codeLength) return

    let newCodes: string[]

    // support copy from clipboard
    if (text.length === codeLength) {
      newCodes = text.split("")
      setCodes(newCodes)
      refs.current[refs.current.length - 1]?.focus()
    } else {
      newCodes = [...codes]
      newCodes[index] = text
      setCodes(newCodes)
      if (text !== "" && index < refs.current.length - 1) {
        refs.current[index + 1]?.focus()
      }
    }

    const fullCode = newCodes.join("")
    const isCodeEntered = fullCode.length === codeLength

    if (isCodeEntered) onCodeEntered(fullCode)
  }

  return (
    <div className={styles.codeInputWrapper} data-testid="confirmation-code-input-container">
      {defaultCodes.map((_, index) => {
        const value = codes[index]

        return (
          <input
            key={index}
            ref={(el) => (refs.current[index] = el)}
            onChange={(e) => onChangeCode(e.target.value, index)}
            onKeyDown={(e) => {
              if (e.key === "Backspace") {
                if (codes[index]) {
                  onChangeCode("", index)
                } else if (index !== refs.current.length - 1) {
                  onChangeCode("", index - 1)
                }

                if (index > 0) refs.current[index - 1]?.focus()
              } else if (e.key.match(/^\d/)) {
                if (codes[index] && codes[index] !== e.key) onChangeCode(e.key, index)
              }
            }}
            value={value}
            readOnly={isReadOnly}
            maxLength={index === 0 ? codes.length : 1}
            autoFocus={index === 0}
            autoCorrect="off"
            autoCapitalize="off"
            inputMode="numeric"
            data-testid={`confirmation-code-input-${index}`}
            className={styles.codeInput}
          />
        )
      })}
    </div>
  )
}
